import React, { useState } from 'react'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { IconButton } from '@material-ui/core'
import { FileCopy as FileCopyIcon } from '@material-ui/icons'
import { useSnackbar } from 'notistack'

import { Content, PageHeader } from '../../components/Layout'
import { CreateFab, Loader } from '../../components/UI'
import { DataList, EmptyDataMessage } from '../../components/Data'
import { CorporatesFormModal } from './components/CorporatesFormModal'
import { ConfirmationDialog } from './components/ConfirmationDialog'
import {
  CREATE_CORPORATION,
  UPDATE_CORPORATIONS,
  DELETE_CORPORATION,
  GET_CORPORATIONS_BY_ADMIN,
} from '../../graphql/queries/businesses'
import { GetCorporationsDataByAdmin } from '../../graphql/types/businesses'

const Corporates: React.FC = () => {
  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [selectedCorporation, setSelectedCorporation] = useState<{ id: string; corporationName: string } | null>(null)
  const [isDeleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false)
  const [corporationToDelete, setCorporationToDelete] = useState<{ id: string; corporationName: string } | null>(null)
  const { enqueueSnackbar } = useSnackbar()

  const { data, loading, error } = useQuery<GetCorporationsDataByAdmin>(GET_CORPORATIONS_BY_ADMIN, {
    fetchPolicy: 'network-only',
  })

  const [createCorporation] = useMutation(CREATE_CORPORATION, {
    refetchQueries: [{ query: GET_CORPORATIONS_BY_ADMIN }],
  })

  const [updateCorporation] = useMutation(UPDATE_CORPORATIONS, {
    refetchQueries: [{ query: GET_CORPORATIONS_BY_ADMIN }],
  })

  const [deleteCorporation] = useMutation(DELETE_CORPORATION, {
    refetchQueries: [{ query: GET_CORPORATIONS_BY_ADMIN }],
  })

  const toggleModal = () => {
    setModalVisible(!modalVisible)
  }

  const handleCreateOrUpdateCorporation = async (id: string | null, corporationName: string) => {
    if (id) {
      await updateCorporation({ variables: { id, corporationName } })
    } else {
      await createCorporation({ variables: { corporationName } })
    }
    toggleModal()
  }

  const handleEdit = (corporation: { id: string; corporationName: string }) => {
    setSelectedCorporation(corporation)
    toggleModal()
  }

  const handleDelete = async () => {
    if (corporationToDelete) {
      await deleteCorporation({ variables: { id: corporationToDelete.id } })
      setDeleteDialogVisible(false)
      setCorporationToDelete(null)
    }
  }

  const openDeleteDialog = (corporation: { id: string; corporationName: string }) => {
    setCorporationToDelete(corporation)
    setDeleteDialogVisible(true)
  }

  const handleCopy = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token)
      enqueueSnackbar('Код скопирован')
    } catch (error) {
      enqueueSnackbar('Не удалось скопировать код')
    }
  }

  if (loading) return <Loader />
  if (error) return <p>Error: {error.message}</p>

  return (
    <div>
      <PageHeader title='Корпорации' />
      {data?.getCorporationsByAdmin.length === 0 && !loading ? (
        <EmptyDataMessage />
      ) : (
        <Content noGutter>
          <DataList
            data={data?.getCorporationsByAdmin || []}
            title='corporationName'
            secondaryText={item => (
              <span style={{ display: 'flex', alignItems: 'center' }}>
                Корпоративный код: {item.token}
                <IconButton aria-label='copy' onClick={() => handleCopy(item.token)}>
                  <FileCopyIcon fontSize='small' />
                </IconButton>
              </span>
            )}
            onEdit={handleEdit}
            onDelete={openDeleteDialog}
          />
        </Content>
      )}
      <CreateFab onClick={toggleModal} />
      {modalVisible && (
        <CorporatesFormModal
          open={modalVisible}
          onCancel={() => {
            toggleModal()
            setSelectedCorporation(null)
          }}
          onSubmit={handleCreateOrUpdateCorporation}
          initialData={selectedCorporation}
        />
      )}
      {isDeleteDialogVisible && (
        <ConfirmationDialog
          open={isDeleteDialogVisible}
          onCancel={() => setDeleteDialogVisible(false)}
          onConfirm={handleDelete}
          message={`Вы уверены, что хотите удалить корпорацию "${corporationToDelete?.corporationName}"?`}
        />
      )}
    </div>
  )
}

export default Corporates
